import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Heart, MessageCircle, Share2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const PostCard = ({ post, onLike }) => {
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  return (
    <Card className="border-stone-200 rounded-2xl post-card bg-white" data-testid={`post-card-${post.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <Link to={`/profile/${post.author_id}`} className="flex items-center gap-3 group">
            <Avatar className="h-10 w-10">
              <AvatarImage src={post.author_avatar} />
              <AvatarFallback className="bg-[#6B8E7B] text-white text-sm">
                {getInitials(post.author_name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-stone-800 group-hover:text-[#6B8E7B] transition-colors">
                {post.author_name}
              </p>
              <div className="flex items-center gap-2 text-sm text-stone-500">
                {post.author_specialization && (
                  <>
                    <span>{post.author_specialization}</span>
                    <span>•</span>
                  </>
                )}
                <span>{formatDate(post.created_at)}</span>
              </div>
            </div>
          </Link>
          {post.category && (
            <Badge variant="outline" className="rounded-full text-xs font-normal text-stone-500 border-stone-200">
              {post.category}
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pb-3">
        <Link to={`/post/${post.id}`} data-testid={`post-title-${post.id}`}>
          <h3 className="text-lg font-semibold text-stone-800 mb-2 hover:text-[#6B8E7B] transition-colors">
            {post.title}
          </h3>
        </Link>
        <p className="text-stone-600 leading-relaxed line-clamp-3">
          {post.content}
        </p>
      </CardContent>
      
      <CardFooter className="pt-3 border-t border-stone-100">
        <div className="flex items-center gap-2 w-full">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onLike(post.id)}
            className="rounded-full text-stone-500 hover:text-[#E27D60] hover:bg-[#E27D60]/10"
            data-testid={`like-button-${post.id}`}
          >
            <Heart className="h-4 w-4 mr-1" />
            {post.likes_count || 0}
          </Button>
          <Link to={`/post/${post.id}`}>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full text-stone-500 hover:text-[#6B8E7B] hover:bg-[#6B8E7B]/10"
              data-testid={`comment-button-${post.id}`}
            >
              <MessageCircle className="h-4 w-4 mr-1" />
              {post.comments_count || 0}
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full text-stone-500 hover:text-stone-700 hover:bg-stone-100 ml-auto"
            data-testid={`share-button-${post.id}`}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default PostCard;
